import { createTheme, ThemeOptions } from '@mui/material/styles';
import { deDE, enUS } from '@mui/material/locale';

const shared: ThemeOptions = {
  typography: {
    fontFamily: [
      '"IBM Plex Sans"',
      '"Segoe UI"',
      'Roboto',
      'Helvetica',
      'Arial',
      'sans-serif',
    ].join(','),
    h4: { fontWeight: 650, letterSpacing: '-0.02em' },
    h5: { fontWeight: 600, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: { root: { borderRadius: 10 } },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 999, fontWeight: 600 } },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'transparent' },
    },
    MuiFab: {
      styleOverrides: { root: { boxShadow: 'none' } },
    },
  },
};

export function createAppTheme(mode: 'light' | 'dark', locale: 'de' | 'en') {
  const isDark = mode === 'dark';
  const muiLocale = locale === 'de' ? deDE : enUS;

  return createTheme(
    {
      ...shared,
      palette: {
        mode,
        primary: {
          main: isDark ? '#5B8CFF' : '#2F6FED',
          contrastText: '#ffffff',
        },
        secondary: {
          main: isDark ? '#7C8AA5' : '#5B6B86',
        },
        success: {
          main: isDark ? '#3DDC97' : '#1FA971',
        },
        warning: {
          main: isDark ? '#FFB020' : '#E08900',
        },
        error: {
          main: isDark ? '#FF5C6C' : '#D64550',
        },
        info: {
          main: isDark ? '#5B8CFF' : '#2F6FED',
        },
        background: isDark
          ? { default: '#0F1419', paper: '#171C24' }
          : { default: '#F3F5F8', paper: '#FFFFFF' },
        divider: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,20,25,0.08)',
        text: isDark
          ? { primary: '#F2F5F8', secondary: '#9AA6B2' }
          : { primary: '#15202B', secondary: '#5B6B86' },
      },
      components: {
        ...shared.components,
        MuiCard: {
          defaultProps: { elevation: 0 },
          styleOverrides: {
            root: {
              borderRadius: 14,
              border: '1px solid',
              borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,20,25,0.08)',
              backgroundImage: 'none',
            },
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              borderRight: '1px solid',
              borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,20,25,0.08)',
              backgroundColor: isDark ? '#12171E' : '#FFFFFF',
              backgroundImage: 'none',
            },
          },
        },
        MuiBottomNavigation: {
          styleOverrides: {
            root: {
              borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,20,25,0.08)'}`,
              backgroundColor: isDark ? '#12171E' : '#FFFFFF',
            },
          },
        },
        MuiPaper: {
          defaultProps: { elevation: 0 },
          styleOverrides: {
            root: { backgroundImage: 'none' },
            outlined: {
              borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,20,25,0.08)',
            },
          },
        },
      },
    },
    muiLocale
  );
}
